/**
 * Module: md-config-ui-cart-error-modal.js
 * Purpose: Shows a dismissable error modal when Add to Cart fails
 * Golden Standard Reference: md-config-ui-confetti-manager.js, md-config-bl-material-selector.js (200/200)
 * Target Compliance Score: 190+/200 (EXEMPLARY)
 *
 * PPD-FIRST ARCHITECTURE (Phase 2 Event-Driven UI Module)
 *
 * Shows a dismissable error modal when the Add to Cart operation fails.
 * Displays customer-friendly contact info plus a techy error details box
 * with a copy-to-clipboard button.
 *
 * BEHAVIOR:
 * - Subscribes to BUSINESS_CART_ADD_FAILED + ERROR_CART_PREPARATION_FAILED at module load time
 * - Shows modal with formatted error details on first failure; replaces details on subsequent failures
 * - Deduplicates events within 200ms window (preparation failure publishes two events in quick succession)
 * - "Dismiss" closes the modal
 * - "Try Again" closes the modal and programmatically re-clicks the Add to Cart button
 * - Copy button copies error details to clipboard
 *
 * CCP COMPLIANCE:
 * - Pure subscriber - never calls other modules directly
 * - DOM manipulation is allowed (this is a ui- module)
 * - Bootstrap-queued via Pattern 6 (module-load-time subscription)
 * - Uses CONSTANTS.EVENTS.* for all event references (Pattern 19)
 * - DOM element references cached at initialize() for O(1) subsequent access
 * - XSS-safe: uses .textContent exclusively for dynamic content (Pattern 17)
 *
 * 5-STAGE EVENT LIFECYCLE:
 * - showErrorModal: START -> SUCCESS/FAILED
 * - dismiss: DISMISSED event
 * - retry: RETRY event
 * - copy: COPY_SUCCESS/COPY_FAILED events
 */
(function (window) {
  'use strict';

  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};

  // ============================================================================
  // PATTERN #1 & #19: CONSTANTS.EVENTS.* Usage (No local EVENT_TYPES - DRY)
  // ============================================================================
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;
  const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;
  const EVENTS = CONSTANTS ? CONSTANTS.EVENTS : {};

  // Dev-only logging (production guard)
  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // ============================================================================
  // PATTERN #16: Initialization Guard (10 points)
  // ============================================================================
  let isInitialized = false;

  // ============================================================================
  // PATTERN #6: Bootstrap Runtime Protection (10 points)
  // ============================================================================
  let bootstrapComplete = false;
  let pendingCalls = [];

  // ============================================================================
  // PATTERN #15: Cleanup Arrays (10 points)
  // ============================================================================
  const cleanupHandlers = [];

  // Cached DOM references (set in initialize())
  let _modalEl = null;
  let _detailsEl = null;
  let _copyBtn = null;
  let _addToCartBtn = null;
  // Cart-review state (partial add whose rollback failed): the notice and
  // "View Cart" link replace "Try Again", which would add a second copy.
  let _retryBtn = null;
  let _cartNoticeEl = null;
  let _viewCartEl = null;
  let _baseDescribedBy = null;
  let _needsCartReview = false;

  // Dialog accessibility state (WCAG 2.4.3 Focus Order, 2.1.2 No Keyboard Trap).
  // _openerEl: the element focused when the modal opened, so focus can return
  // to it on close. _isOpen: guards the "replace details" re-show path from
  // overwriting the opener with an element inside the modal.
  let _openerEl = null;
  let _isOpen = false;

  const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), ' +
    'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  // Dedup guard: ignore second error event if it fires within 200ms of the first
  // (cart-manager may publish BOTH BUSINESS_CART_ADD_FAILED + ERROR_CART_PREPARATION_FAILED
  // for the same user action when preparation fails, causing a double-show flicker)
  let _lastErrorTimestamp = 0;
  const ERROR_DEDUP_WINDOW_MS = 200;

  // ============================================================================
  // PATTERN #2: publishEvent() Helper (10 points)
  // NOTE: Object.freeze() NOT needed - CoreEventBus.publish() freezes centrally (Pattern #18 DRY)
  // ============================================================================
  function publishEvent(eventType, payload = {}) {
    try {
      if (CoreEventBus) {
        CoreEventBus.publish(eventType, payload);
      }
    } catch (error) {
      isDev() &&
        console.error(
          '[CartErrorModal] Event publish failed:',
          eventType,
          error
        );
    }
  }

  // ============================================================================
  // PATTERN #12 & #13: Performance Tracking with Thresholds (20 points)
  // ============================================================================
  const PERFORMANCE_THRESHOLDS = {
    MODAL_SHOW: 50, // ms - modal display should be fast
    CLIPBOARD_COPY: 200, // ms - clipboard operations
    INITIALIZATION: 100, // ms - module init
  };

  function trackPerformance(operationType, duration) {
    const threshold = PERFORMANCE_THRESHOLDS[operationType];
    if (threshold && duration > threshold) {
      publishEvent(EVENTS.PERFORMANCE_SLOW_OPERATION, {
        module: 'cart-error-modal',
        operation: operationType,
        duration: Math.round(duration * 100) / 100,
        threshold,
        severity: duration > threshold * 2 ? 'critical' : 'warning',
        timestamp: Date.now(),
      });
    }
  }

  // ============================================================================
  // PATTERN #6: Bootstrap Runtime Protection - Module-load-time subscription
  // Subscribe at module load time for maximum race protection
  // ============================================================================
  if (CoreEventBus && CONSTANTS) {
    CoreEventBus.subscribe(EVENTS.SYSTEM_STATE_FROM_PPD_READY, () => {
      isDev() &&
        console.log(
          '[CartErrorModal] Bootstrap complete - processing queued calls'
        );
      bootstrapComplete = true;

      if (pendingCalls.length > 0) {
        isDev() &&
          console.log(
            `[CartErrorModal] Processing ${pendingCalls.length} queued operations`
          );
        pendingCalls.forEach((call) => {
          try {
            call.fn.apply(call.context, call.args);
          } catch (error) {
            isDev() &&
              console.error(
                '[CartErrorModal] Error processing queued call:',
                error
              );
          }
        });
        pendingCalls = [];
      }
    });
    isDev() &&
      console.log(
        '[CartErrorModal] Subscribed to bootstrap event at module load time'
      );
  }

  // ============================================================================
  // DOM HELPERS
  // ============================================================================

  /**
   * Formats an error event payload into a human-readable string for the
   * techy details box.
   *
   * PATTERN #17: XSS Prevention - output is set via .textContent (not .innerHTML)
   *
   * @param {Object} eventData - Error event payload from cart-manager
   * @returns {string} Formatted error text
   */
  function formatErrorDetails(eventData) {
    const lines = [];

    if (eventData.error) {
      lines.push(`Error:   ${String(eventData.error)}`);
    }
    if (eventData.source) {
      lines.push(`Source:  ${String(eventData.source)}`);
    }
    if (eventData.timestamp) {
      const d = new Date(eventData.timestamp);
      lines.push(`Time:    ${d.toISOString()}`);
    }

    // Include any extra keys that carry useful debug info
    const knownKeys = new Set(['error', 'source', 'timestamp', 'source_event']);
    Object.entries(eventData).forEach(([key, value]) => {
      if (!knownKeys.has(key) && value !== undefined && value !== null) {
        let formatted;
        try {
          formatted =
            typeof value === 'object'
              ? JSON.stringify(value, null, 2)
              : String(value);
        } catch (e) {
          formatted = '[Could not serialize object]';
        }
        lines.push(`${key.padEnd(8)} ${formatted}`);
      }
    });

    return lines.length > 0 ? lines.join('\n') : 'No error details available.';
  }

  // ============================================================================
  // PATTERN #3: 5-Stage Event Lifecycle for Modal Show (10 points)
  // PATTERN #14: Try/Catch + Failed Events (10 points)
  // ============================================================================

  /**
   * Shows (or updates) the cart error modal with the given error details.
   * Uses "replace" behavior: if modal is already visible, details are refreshed.
   *
   * Lifecycle: START -> SUCCESS | FAILED
   *
   * @param {Object} eventData - Error event payload
   */
  function showErrorModal(eventData) {
    const startTime = performance.now();

    try {
      // STAGE 1: START
      publishEvent(EVENTS.UI_CART_ERROR_MODAL_SHOW_START, {
        hasModalElement: !!_modalEl,
        timestamp: Date.now(),
      });

      if (!_modalEl) {
        isDev() &&
          console.error(
            '[CartErrorModal] Modal not initialized - call initialize() first'
          );
        throw new Error('Modal DOM element not initialized');
      }

      // PATTERN #17: XSS Prevention - uses .textContent, NOT .innerHTML
      if (_detailsEl) {
        _detailsEl.textContent = formatErrorDetails(eventData);
      }

      _applyCartReviewState(!!(eventData && eventData.cartNeedsReview === true));

      // Reset copy button state if it was previously in "copied" state
      // PATTERN #17: XSS Prevention - uses .textContent for button label
      if (_copyBtn) {
        _copyBtn.textContent = 'Copy';
        _copyBtn.classList.remove(
          'murphy-door-configurator__cart-error-copy-button--copied'
        );
      }

      _modalEl.style.display = 'flex';

      // Dialog semantics: remember the opener (first show only - a re-show just
      // refreshes details), move focus inside, trap Tab, Escape closes.
      if (!_isOpen) {
        const active = document.activeElement;
        _openerEl =
          active && active !== document.body && !_modalEl.contains(active)
            ? active
            : null;
        _isOpen = true;
        _modalEl.addEventListener('keydown', _handleModalKeydown);
      }
      _focusModal();

      // STAGE 4: SUCCESS
      const duration = performance.now() - startTime;
      publishEvent(EVENTS.UI_CART_ERROR_MODAL_SHOW_SUCCESS, {
        duration: Math.round(duration * 100) / 100,
        timestamp: Date.now(),
      });

      // PATTERN #12: Performance Tracking
      trackPerformance('MODAL_SHOW', duration);

      isDev() && console.log('[CartErrorModal] Error modal shown');
    } catch (error) {
      // STAGE 5: FAILED
      const duration = performance.now() - startTime;
      publishEvent(EVENTS.UI_CART_ERROR_MODAL_SHOW_FAILED, {
        error: error.message,
        duration: Math.round(duration * 100) / 100,
        timestamp: Date.now(),
      });

      isDev() &&
        console.error('[CartErrorModal] showErrorModal failed:', error);
    }
  }

  /**
   * Visible, focusable controls inside the modal, in DOM order.
   * @private
   */
  function _getFocusable() {
    if (!_modalEl) return [];
    // A [hidden] control (Try Again or View Cart, whichever is swapped out)
    // must not become a Tab-wrap target.
    return Array.from(_modalEl.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (node) => !node.closest('[hidden]')
    );
  }

  /**
   * Switches the modal between its two endings, driven ONLY by the failure
   * payload (cart-manager owns the cart; this module just reads its verdict):
   * - normal: "Try Again" is offered.
   * - cartNeedsReview: a partial add could not be rolled back, so lines from
   *   that attempt may still be in the cart. The notice tells the shopper to
   *   check the cart, "View Cart" links to /cart, and "Try Again" is hidden
   *   because re-posting would add a second copy. The notice joins the
   *   dialog's description so the alertdialog announces it on open (C2).
   * @param {boolean} needsReview
   * @private
   */
  function _applyCartReviewState(needsReview) {
    _needsCartReview = needsReview;
    if (_cartNoticeEl) _cartNoticeEl.hidden = !needsReview;
    if (_viewCartEl) _viewCartEl.hidden = !needsReview;
    if (_retryBtn) _retryBtn.hidden = needsReview;
    if (_modalEl && _cartNoticeEl && _cartNoticeEl.id) {
      const base = _baseDescribedBy || '';
      const value = needsReview
        ? `${_cartNoticeEl.id} ${base}`.trim()
        : base;
      if (value) _modalEl.setAttribute('aria-describedby', value);
      else _modalEl.removeAttribute('aria-describedby');
    }
  }

  /**
   * Keyboard handling while the modal is open: Escape dismisses, Tab and
   * Shift+Tab wrap inside the modal so keyboard and screen-reader focus cannot
   * wander into the configurator behind it.
   * @private
   */
  function _handleModalKeydown(event) {
    if (!_isOpen) return;
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      handleDismiss();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = _getFocusable();
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !_modalEl.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (
      !event.shiftKey &&
      (active === last || !_modalEl.contains(active))
    ) {
      event.preventDefault();
      first.focus();
    }
  }

  /**
   * Moves focus into the modal on open so VoiceOver announces the alertdialog
   * (its label and description) instead of staying silent on the Add to Cart
   * button. Focus goes to the least destructive action, Dismiss, per the ARIA
   * alertdialog pattern; falls back to the title.
   * @private
   */
  function _focusModal() {
    if (!_modalEl) return;
    const dismissBtn = _modalEl.querySelector(
      '.murphy-door-configurator__cart-error-dismiss'
    );
    if (dismissBtn) {
      dismissBtn.focus();
      return;
    }
    const title = _modalEl.querySelector('#cart-error-modal-title');
    if (title) {
      if (!title.hasAttribute('tabindex')) title.setAttribute('tabindex', '-1');
      title.focus();
    }
  }

  /**
   * Hides the cart error modal and returns focus to where it was on open
   * (normally the Add to Cart button).
   */
  function hideErrorModal() {
    if (_modalEl) {
      _modalEl.style.display = 'none';
      _modalEl.removeEventListener('keydown', _handleModalKeydown);
    }
    if (!_isOpen) return;
    _isOpen = false;
    const returnTo =
      _openerEl && _openerEl.isConnected ? _openerEl : _addToCartBtn;
    _openerEl = null;
    if (returnTo && typeof returnTo.focus === 'function') {
      try {
        returnTo.focus();
      } catch (e) {
        /* focus is best-effort */
      }
    }
  }

  // ============================================================================
  // BUTTON HANDLERS WITH EVENT LIFECYCLE
  // ============================================================================

  /**
   * Handles the "Dismiss" button click - closes the modal.
   * Publishes DISMISSED event for system coordination.
   */
  function handleDismiss() {
    try {
      hideErrorModal();

      // PATTERN #10: UI Update Events
      publishEvent(EVENTS.UI_CART_ERROR_MODAL_DISMISSED, {
        action: 'dismiss',
        timestamp: Date.now(),
      });

      isDev() && console.log('[CartErrorModal] Dismissed by user');
    } catch (error) {
      isDev() && console.error('[CartErrorModal] handleDismiss failed:', error);
    }
  }

  /**
   * Handles the "Try Again" button click.
   * Closes the modal and programmatically re-fires the Add to Cart button click,
   * which triggers the exact same flow as the user clicking it manually.
   *
   * If the ATC button is disabled (configuration incomplete), the modal is still
   * closed but no click is fired - the user sees the configurator and understands
   * they need to complete their selections.
   */
  function handleRetry() {
    try {
      hideErrorModal();

      // Defensive: the button is hidden in this state, but a re-post here
      // would add a second copy of lines that may still be in the cart.
      if (_needsCartReview) {
        publishEvent(EVENTS.UI_CART_ERROR_MODAL_RETRY, {
          action: 'retry',
          success: false,
          reason: 'cart_needs_review',
          timestamp: Date.now(),
        });
        return;
      }

      if (!_addToCartBtn) {
        isDev() &&
          console.warn(
            '[CartErrorModal] Add to Cart button not found for retry'
          );
        publishEvent(EVENTS.UI_CART_ERROR_MODAL_RETRY, {
          action: 'retry',
          success: false,
          reason: 'button_not_found',
          timestamp: Date.now(),
        });
        return;
      }

      if (_addToCartBtn.disabled) {
        isDev() &&
          console.warn(
            '[CartErrorModal] Add to Cart button is disabled - configuration incomplete, retry aborted'
          );
        publishEvent(EVENTS.UI_CART_ERROR_MODAL_RETRY, {
          action: 'retry',
          success: false,
          reason: 'button_disabled',
          timestamp: Date.now(),
        });
        return;
      }

      isDev() &&
        console.log(
          '[CartErrorModal] Retrying - programmatically clicking Add to Cart button'
        );

      // PATTERN #10: UI Update Events - publish retry event
      publishEvent(EVENTS.UI_CART_ERROR_MODAL_RETRY, {
        action: 'retry',
        success: true,
        timestamp: Date.now(),
      });

      _addToCartBtn.click();
    } catch (error) {
      isDev() && console.error('[CartErrorModal] handleRetry failed:', error);
    }
  }

  /**
   * Handles the "Copy" button click - copies error details to clipboard.
   * Publishes COPY_SUCCESS or COPY_FAILED for system coordination.
   */
  function handleCopy() {
    if (!_detailsEl || !_copyBtn) return;

    const text = _detailsEl.textContent;
    if (!text) return;

    const startTime = performance.now();

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          _setCopiedState(_copyBtn);
          const duration = performance.now() - startTime;
          publishEvent(EVENTS.UI_CART_ERROR_MODAL_COPY_SUCCESS, {
            method: 'clipboard_api',
            duration: Math.round(duration * 100) / 100,
            timestamp: Date.now(),
          });
          trackPerformance('CLIPBOARD_COPY', duration);
        })
        .catch((err) => {
          isDev() &&
            console.warn('[CartErrorModal] Clipboard copy failed:', err);
          _fallbackCopy(text, _copyBtn, startTime);
        });
    } else {
      _fallbackCopy(text, _copyBtn, startTime);
    }
  }

  /**
   * Applies "Copied!" visual state to the copy button, then resets after 2s.
   * PATTERN #17: XSS Prevention - uses .textContent for button label
   * @private
   */
  function _setCopiedState(copyBtn) {
    copyBtn.textContent = 'Copied!';
    copyBtn.classList.add(
      'murphy-door-configurator__cart-error-copy-button--copied'
    );

    setTimeout(() => {
      copyBtn.textContent = 'Copy';
      copyBtn.classList.remove(
        'murphy-door-configurator__cart-error-copy-button--copied'
      );
    }, 2000);
  }

  /**
   * Fallback copy using execCommand for older browsers.
   * @private
   */
  function _fallbackCopy(text, copyBtn, startTime) {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      _setCopiedState(copyBtn);

      const duration = performance.now() - startTime;
      publishEvent(EVENTS.UI_CART_ERROR_MODAL_COPY_SUCCESS, {
        method: 'execCommand_fallback',
        duration: Math.round(duration * 100) / 100,
        timestamp: Date.now(),
      });
      trackPerformance('CLIPBOARD_COPY', duration);
    } catch (err) {
      isDev() &&
        console.warn('[CartErrorModal] Fallback copy also failed:', err);
      const duration = performance.now() - startTime;
      publishEvent(EVENTS.UI_CART_ERROR_MODAL_COPY_FAILED, {
        error: err.message,
        duration: Math.round(duration * 100) / 100,
        timestamp: Date.now(),
      });
    }
  }

  // ============================================================================
  // PATTERN #4: Event Consumer Handler (10 points)
  // CART ERROR EVENT HANDLER
  // ============================================================================

  /**
   * Handles incoming cart error events. Shows the modal with error details.
   *
   * Guards against:
   * - Pre-bootstrap calls (via pending queue - Pattern #6)
   * - Double-fire within 200ms window (cart-manager publishes two events for
   *   preparation failures: ERROR_CART_PREPARATION_FAILED + BUSINESS_CART_ADD_FAILED)
   *
   * @param {Object} eventData - Error event payload from cart-manager
   */
  function handleCartError(eventData) {
    // PATTERN #6: Bootstrap Runtime Protection
    if (!bootstrapComplete) {
      pendingCalls.push({
        fn: handleCartError,
        context: null,
        args: [eventData],
      });
      return;
    }

    // Dedup: ignore rapid successive events from the same user action
    const now = Date.now();
    if (now - _lastErrorTimestamp < ERROR_DEDUP_WINDOW_MS) {
      isDev() &&
        console.log(
          '[CartErrorModal] Ignoring duplicate error event within dedup window'
        );
      return;
    }
    _lastErrorTimestamp = now;

    isDev() &&
      console.log(
        '[CartErrorModal] Cart error received, showing modal:',
        eventData
      );
    showErrorModal(eventData || {});
  }

  // ============================================================================
  // INITIALIZATION WITH LIFECYCLE
  // PATTERN #16: Initialization Guard (10 points)
  // ============================================================================

  /**
   * Initializes the cart error modal module:
   * - Caches DOM element references for efficient subsequent access
   * - Binds button click handlers on the modal DOM
   * - Subscribes to cart error events on CoreEventBus
   *
   * PATTERN #5: Event Replay Ready - subscribes to cart error events
   * that may have already fired (handled via bootstrap queue)
   */
  function initialize() {
    // PATTERN #16: Initialization Guard
    if (isInitialized) {
      isDev() &&
        console.warn('[CartErrorModal] Already initialized - skipping');
      return;
    }

    const startTime = performance.now();

    try {
      _modalEl = document.querySelector(
        '.murphy-door-configurator__cart-error-modal'
      );
      if (!_modalEl) {
        isDev() &&
          console.error(
            '[CartErrorModal] initialize() - modal DOM element not found. ' +
              'Ensure md-configurator.liquid includes .murphy-door-configurator__cart-error-modal'
          );
        return;
      }

      // Cache child element references once (O(1) on subsequent access)
      _detailsEl = _modalEl.querySelector(
        '.murphy-door-configurator__cart-error-details'
      );
      _copyBtn = _modalEl.querySelector(
        '.murphy-door-configurator__cart-error-copy-button'
      );
      _addToCartBtn = document.querySelector(
        '.murphy-door-configurator__add-to-cart-button'
      );
      const dismissBtn = _modalEl.querySelector(
        '.murphy-door-configurator__cart-error-dismiss'
      );
      const retryBtn = _modalEl.querySelector(
        '.murphy-door-configurator__cart-error-retry'
      );
      _retryBtn = retryBtn;
      _cartNoticeEl = _modalEl.querySelector(
        '.murphy-door-configurator__cart-error-cart-notice'
      );
      _viewCartEl = _modalEl.querySelector(
        '.murphy-door-configurator__cart-error-view-cart'
      );
      _baseDescribedBy = _modalEl.getAttribute('aria-describedby');

      // Bind button handlers
      if (dismissBtn) dismissBtn.addEventListener('click', handleDismiss);
      if (retryBtn) retryBtn.addEventListener('click', handleRetry);
      if (_copyBtn) _copyBtn.addEventListener('click', handleCopy);

      // PATTERN #4: Event Consumer Handlers - Subscribe to cart error events
      // PATTERN #19: CONSTANTS.EVENTS.* usage (no local EVENT_TYPES or magic strings)
      if (CoreEventBus && CONSTANTS) {
        // Primary: add-to-cart failure (covers most scenarios)
        const unsub1 = CoreEventBus.subscribe(
          EVENTS.BUSINESS_CART_ADD_FAILED,
          handleCartError
        );
        cleanupHandlers.push(unsub1);

        // Secondary: preparation failure (fires before ADD_FAILED in some cases;
        // dedup guard prevents double-modal when both fire for the same action)
        const unsub2 = CoreEventBus.subscribe(
          EVENTS.ERROR_CART_PREPARATION_FAILED,
          handleCartError
        );
        cleanupHandlers.push(unsub2);
      } else {
        isDev() &&
          console.error(
            '[CartErrorModal] CoreEventBus or CONSTANTS not available during initialize()'
          );
      }

      isInitialized = true;

      const duration = performance.now() - startTime;

      // Publish module ready event
      publishEvent(EVENTS.UI_CART_ERROR_MODAL_READY, {
        module: 'md-config-ui-cart-error-modal',
        hasModal: !!_modalEl,
        hasDetails: !!_detailsEl,
        hasCopyBtn: !!_copyBtn,
        hasAddToCartBtn: !!_addToCartBtn,
        duration: Math.round(duration * 100) / 100,
        timestamp: Date.now(),
      });

      // PATTERN #12: Performance Tracking
      trackPerformance('INITIALIZATION', duration);

      isDev() &&
        console.log(
          '[CartErrorModal] Initialized - subscribed to cart error events'
        );
    } catch (error) {
      isDev() &&
        console.error('[CartErrorModal] Initialization failed:', error);
    }
  }

  // ============================================================================
  // CLEANUP
  // PATTERN #15: Cleanup Arrays (10 points)
  // ============================================================================

  /**
   * Cleans up all event subscriptions and resets module state.
   */
  function cleanup() {
    // PATTERN #15: Cleanup all tracked subscriptions
    cleanupHandlers.forEach((unsubscribe) => {
      try {
        unsubscribe();
      } catch (err) {
        isDev() && console.error('[CartErrorModal] Cleanup error:', err);
      }
    });
    cleanupHandlers.length = 0;

    if (_modalEl) {
      _modalEl.removeEventListener('keydown', _handleModalKeydown);
    }
    _isOpen = false;
    _openerEl = null;
    isInitialized = false;
    _modalEl = null;
    _detailsEl = null;
    _copyBtn = null;
    _addToCartBtn = null;
    _retryBtn = null;
    _cartNoticeEl = null;
    _viewCartEl = null;
    _baseDescribedBy = null;
    _needsCartReview = false;

    publishEvent(EVENTS.UI_CART_ERROR_MODAL_CLEANUP, {
      module: 'md-config-ui-cart-error-modal',
      timestamp: Date.now(),
    });

    isDev() && console.log('[CartErrorModal] Cleaned up');
  }

  // ============================================================================
  // PATTERN #20: Conductor Single Publication - PUBLIC API
  // ============================================================================
  window.MurphyDoorConfigurator.cartErrorModal = {
    initialize,
    cleanup,
    showErrorModal,
    hideErrorModal,
  };

  // ============================================================================
  // AUTO-INITIALIZE
  // Initialize when DOM is ready (handles both deferred and inline loading)
  // ============================================================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }
})(window);
